/**
 * What a project is classified as, from its first prompt. A MIRROR of
 * account-backend's `common/constants/project-category.ts` — keys and labels
 * must stay identical; the backend list is authoritative and also arrives with
 * the CEO filter options (`options.projectCategories`) for the dropdowns. This
 * copy exists so a chip can be rendered without waiting for that round trip.
 */

export type ProjectCategoryGroup = 'app' | 'website' | 'commerce' | 'tool' | 'game' | 'non_build' | 'other';

export interface ProjectCategoryDefinitionI {
    key: string;
    label: string;
    group: ProjectCategoryGroup;
}

export const PROJECT_CATEGORIES: readonly ProjectCategoryDefinitionI[] = [
    { key: 'saas', label: 'SaaS / web app', group: 'app' },
    { key: 'internal_tool', label: 'Internal tool / dashboard', group: 'app' },
    { key: 'crm', label: 'CRM', group: 'app' },
    { key: 'erp', label: 'ERP / inventory / operations', group: 'app' },
    { key: 'booking', label: 'Booking / reservations', group: 'app' },
    { key: 'marketplace', label: 'Marketplace', group: 'commerce' },
    { key: 'ecommerce', label: 'E-commerce / online store', group: 'commerce' },
    { key: 'fintech', label: 'Finance / fintech', group: 'app' },
    { key: 'health', label: 'Health / fitness / medical', group: 'app' },
    { key: 'education', label: 'Education / courses', group: 'app' },
    { key: 'community_social', label: 'Community / social', group: 'app' },
    { key: 'real_estate', label: 'Real estate', group: 'app' },
    { key: 'food_hospitality', label: 'Restaurant / food / hospitality', group: 'app' },
    { key: 'events', label: 'Events / invitations', group: 'website' },
    { key: 'mobile_app', label: 'Mobile app', group: 'app' },
    { key: 'company_website', label: 'Company website', group: 'website' },
    { key: 'startup_website', label: 'Startup website', group: 'website' },
    { key: 'product_landing', label: 'Product landing page', group: 'website' },
    { key: 'portfolio', label: 'Portfolio / personal site', group: 'website' },
    { key: 'blog_content', label: 'Blog / content / media', group: 'website' },
    { key: 'nonprofit', label: 'Non-profit / association', group: 'website' },
    { key: 'ai_chatbot', label: 'AI chatbot / assistant', group: 'tool' },
    { key: 'automation', label: 'Automation / integration / bot', group: 'tool' },
    { key: 'utility_tool', label: 'Utility / calculator / generator', group: 'tool' },
    { key: 'videogame', label: 'Videogame', group: 'game' },
    { key: 'clone', label: 'Clone of a known product', group: 'app' },
    { key: 'just_question', label: 'Just a question', group: 'non_build' },
    { key: 'test_random', label: 'Test / random / meaningless', group: 'non_build' },
    { key: 'other', label: 'Other', group: 'other' },
];

const BY_KEY = new Map(PROJECT_CATEGORIES.map(c => [c.key, c]));

export function projectCategoryLabel(key?: string | null): string {
    if (!key) return 'Unclassified';
    if (key === 'none') return 'Unclassified';
    return BY_KEY.get(key)?.label || key;
}

export function projectCategoryGroup(key?: string | null): ProjectCategoryGroup | null {
    return key ? BY_KEY.get(key)?.group || null : null;
}

/** Chip colours encode the GROUP, so twenty-nine keys read as six families at a glance. */
const GROUP_STYLES: Record<ProjectCategoryGroup, string> = {
    app: 'bg-blue-50 text-blue-700 border-blue-200',
    website: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    commerce: 'bg-amber-50 text-amber-700 border-amber-200',
    tool: 'bg-violet-50 text-violet-700 border-violet-200',
    game: 'bg-pink-50 text-pink-700 border-pink-200',
    non_build: 'bg-slate-100 text-slate-500 border-slate-200',
    other: 'bg-slate-50 text-slate-600 border-slate-200',
};

export function projectCategoryStyle(key?: string | null): string {
    const group = projectCategoryGroup(key);
    if (!group) return 'bg-white text-slate-400 border-dashed border-slate-300';
    return GROUP_STYLES[group];
}

/** Tooltip for a classified project: the model's one-line summary plus how sure it was. */
export function projectCategoryTitle(category?: { key?: string; summary?: string; confidence?: number; source?: string; model?: string } | null): string {
    if (!category?.key) return 'Not classified yet — no first prompt has been seen through VCaaS';
    const parts = [projectCategoryLabel(category.key)];
    if (category.summary) parts.push(category.summary);
    if (typeof category.confidence === 'number') parts.push(`${Math.round(category.confidence * 100)}% sure`);
    if (category.source === 'heuristic') parts.push('keyword fallback (model unavailable)');
    else if (category.model) parts.push(category.model);
    return parts.join(' · ');
}
